/*
Extract and validate tokens in the URL if they are present.
*/
import store from "../store/store";
import router from "../router";
import { useToast } from 'vue-toastification'

const toast = useToast();
const EMAIL_CONFIRMATION_STATUS_KEY = 'kairo_email_confirmation_status';

function publishEmailConfirmationStatus(detail) {
  try {
    window.sessionStorage.setItem(EMAIL_CONFIRMATION_STATUS_KEY, JSON.stringify(detail));
  } catch (error) {
    console.error('Unable to persist email confirmation status.', error);
  }

  window.dispatchEvent(new CustomEvent('kairo:email-confirmation', { detail }));
}

/**
 * Reads the URL hash attempts and tries to detect if there is confirmation tokens from an email signup.
 * If present it will call the relevant process to attempt to authorise the token.
 */
function detectTokens() {
  const supabaseToken = detectSupabaseToken();
  if (supabaseToken?.type === 'signup') {
    confirmEmailToken(supabaseToken.token);
    return;
  }

  if (supabaseToken?.type === 'recovery') {
    confirmRecoveryToken(supabaseToken.token);
    return;
  }

  const emailToken = detectEmailConfirmationToken();
  const recoveryToken = detectRecoveryToken();

  if (emailToken) {
    confirmEmailToken(emailToken);
    return;
  } else if (recoveryToken) {
    confirmRecoveryToken(recoveryToken);
    return;
  }
}

function detectSupabaseToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token_hash');
    const type = params.get('type');

    if (!token || !type) {
      return null;
    }

    return {
      token,
      type,
    };
  } catch (error) {
    console.error('Something went wrong when trying to extract the Supabase auth token.', error);
    return null;
  }
}

/**
 * Checks URL hash for `confirmation_token=` then extracts the token which proceeds.
 */
function detectEmailConfirmationToken() {
  try {
    // split the hash where it detects `confirmation_token=`. The string which proceeds is the part which we want.
    const token = decodeURIComponent(document.location.hash).split(
        "confirmation_token="
    )[1];
    return token;
  } catch (error) {
    console.error(
        "Something went wrong when trying to extract email confirmation email.",
        error
    );
    return null;
  }
}

function detectRecoveryToken() {
  try {
    // split the hash where it detects `confirmation_token=`. The string which proceeds is the part which we want.
    const token = decodeURIComponent(document.location.hash).split(
        "recovery_token="
    )[1];
    return token;
  } catch (error) {
    console.error(
        "Something went wrong when trying to extract email confirmation email",
        error
    );
    return null;
  }
}

/**
 * @param {string} token - authentication token used to confirm a user who has created an account via email signup.
 */
function confirmEmailToken(token) {
  publishEmailConfirmationStatus({
    status: 'verifying',
    message: 'Verifying your email...',
  });

  store
    .dispatch("auth/attemptConfirmation", token)
    .then(resp => {
      window.history.replaceState({}, document.title, window.location.pathname);
      publishEmailConfirmationStatus({
        status: 'success',
        message: `${resp.email} has been verified. Redirecting...`,
      });
      toast.success(`${resp.email} has been confirmed.`);
      router.push({ name: 'Tracker' });
    })
    .catch(() => {
      publishEmailConfirmationStatus({
        status: 'error',
        message: `Can't authorise your account right now. Please try again.`,
      });
      toast.error(`Can't authorise your account right now. Please try again.`);
    });
}

function confirmRecoveryToken(recoveryToken) {
  store
      .dispatch("auth/attemptPasswordRecovery", recoveryToken)
      .then(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        router.push("profile-edit");
        toast.success(`Account has been recovered. Update your password.`);
      })
      .catch(() => {
        toast.error(`Can't recover password.`);
      });
}

export default function() {
  detectTokens();
}
